import React, { useState, useEffect, useRef } from "react";
import {
  Bell,
  X,
  CheckCheck,
  UserCheck,
  Clock,
  CreditCard,
  FileText,
  Sparkles,
  ExternalLink,
  Volume2,
  VolumeX,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import apiClient from "../utils/apiClient";
import { socket } from "../config/socket";

const playChimeSound = () => {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;
    const ctx = new AudioContext();

    const osc1 = ctx.createOscillator();
    const osc2 = ctx.createOscillator();
    const gain = ctx.createGain();

    osc1.type = "sine";
    osc2.type = "sine";

    osc1.frequency.setValueAtTime(523.25, ctx.currentTime); // C5
    osc2.frequency.setValueAtTime(659.25, ctx.currentTime + 0.08); // E5

    gain.gain.setValueAtTime(0.12, ctx.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.4);

    osc1.connect(gain);
    osc2.connect(gain);
    gain.connect(ctx.destination);

    osc1.start(ctx.currentTime);
    osc2.start(ctx.currentTime + 0.08);

    osc1.stop(ctx.currentTime + 0.4);
    osc2.stop(ctx.currentTime + 0.4);
  } catch (err) {
    // Audio context prevented by browser policy until interaction
  }
};

const NotificationBell = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("all");
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [latestToast, setLatestToast] = useState(null);
  const toastTimeoutRef = useRef(null);

  // Fetch notifications from API
  const fetchNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiClient.get("/notifications", {
        params: { limit: 30 },
      });

      if (response.data) {
        const notifList =
          response.data.notifications || response.data.data?.notifications || [];
        const unread =
          response.data.unreadCount ?? response.data.data?.unreadCount ?? 0;

        setNotifications(notifList);
        setUnreadCount(unread);
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
  }, []);

  // Listen for real-time socket events
  useEffect(() => {
    const handleNewNotification = (notif) => {
      setNotifications((prev) => [notif, ...prev]);
      setUnreadCount((prev) => prev + 1);

      if (soundEnabled) {
        playChimeSound();
      }

      // Show temporary floating toast
      setLatestToast(notif);
      if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
      toastTimeoutRef.current = setTimeout(() => {
        setLatestToast(null);
      }, 4500);
    };

    socket.on("notification", handleNewNotification);
    socket.on("activity_notification", handleNewNotification);

    return () => {
      socket.off("notification", handleNewNotification);
      socket.off("activity_notification", handleNewNotification);
    };
  }, [soundEnabled]);

  // Mark single notification as read
  const markAsRead = async (id, actionUrl) => {
    try {
      setNotifications((prev) =>
        prev.map((n) => (n._id === id || n.id === id ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
      await apiClient.put(`/notifications/${id}/read`);
    } catch (error) {
      console.error("Error marking notification read:", error);
    }

    if (actionUrl) {
      setIsOpen(false);
      navigate(actionUrl);
    }
  };

  // Mark all as read
  const markAllAsRead = async () => {
    try {
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      await apiClient.put("/notifications/mark-all-read");
    } catch (error) {
      console.error("Error marking all read:", error);
    }
  };

  // Delete notification
  const deleteNotification = async (id, e) => {
    e?.stopPropagation();
    try {
      setNotifications((prev) =>
        prev.filter((n) => n._id !== id && n.id !== id)
      );
      await apiClient.delete(`/notifications/${id}`);
    } catch (error) {
      console.error("Error deleting notification:", error);
    }
  };

  // Category Icon & Styling Helper
  const getCategoryMeta = (notif) => {
    const cat = (notif.category || notif.type || "").toLowerCase();

    if (cat.includes("assignment") || cat.includes("task")) {
      return {
        icon: <UserCheck className="w-4 h-4 text-blue-600" />,
        badgeBg: "#e0f2fe",
        badgeColor: "#0369a1",
        label: "Lead",
      };
    }
    if (cat.includes("followup") || cat.includes("reminder") || cat.includes("due")) {
      return {
        icon: <Clock className="w-4 h-4 text-amber-600" />,
        badgeBg: "#fef3c7",
        badgeColor: "#b45309",
        label: "Follow-up",
      };
    }
    if (cat.includes("payment")) {
      return {
        icon: <CreditCard className="w-4 h-4 text-emerald-600" />,
        badgeBg: "#dcfce7",
        badgeColor: "#15803d",
        label: "Payment",
      };
    }
    if (cat.includes("quotation") || cat.includes("booking")) {
      return {
        icon: <FileText className="w-4 h-4 text-purple-600" />,
        badgeBg: "#f3e8ff",
        badgeColor: "#7e22ce",
        label: "Quote",
      };
    }

    return {
      icon: <Sparkles className="w-4 h-4 text-indigo-600" />,
      badgeBg: "#e0e7ff",
      badgeColor: "#4338ca",
      label: "System",
    };
  };

  // Time formatter
  const formatTime = (dateString) => {
    if (!dateString) return "Just now";
    const date = new Date(dateString);
    const now = new Date();
    const diffInMins = Math.floor((now - date) / (1000 * 60));

    if (diffInMins < 1) return "Just now";
    if (diffInMins < 60) return `${diffInMins}m ago`;
    if (diffInMins < 1440) return `${Math.floor(diffInMins / 60)}h ago`;
    return `${Math.floor(diffInMins / 1440)}d ago`;
  };

  // Filtered notifications by active tab
  const filteredNotifications = notifications.filter((notif) => {
    if (activeTab === "all") return true;
    const cat = (notif.category || notif.type || "").toLowerCase();
    if (activeTab === "assignments")
      return cat.includes("assignment") || cat.includes("task");
    if (activeTab === "followups")
      return cat.includes("followup") || cat.includes("reminder") || cat.includes("due");
    if (activeTab === "payments") return cat.includes("payment");
    if (activeTab === "activities")
      return (
        cat.includes("quotation") ||
        cat.includes("booking") ||
        cat.includes("system") ||
        cat.includes("alert")
      );
    return true;
  });

  return (
    <div className="relative inline-block">
      {/* HEADER BELL BUTTON */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 rounded-lg text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors focus:outline-none"
        title="Notifications"
      >
        <Bell className="w-5 h-5" />

        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-rose-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-5 w-5 bg-rose-600 text-[10px] font-bold text-white items-center justify-center px-1">
              {unreadCount > 99 ? "99+" : unreadCount}
            </span>
          </span>
        )}
      </button>

      {/* REAL-TIME TOAST ALERT (TOP RIGHT FLOATING) */}
      {latestToast && (
        <div
          onClick={() => {
            if (latestToast.actionUrl) {
              navigate(latestToast.actionUrl);
            }
            setLatestToast(null);
          }}
          className="fixed top-16 right-6 z-[9999] w-80 bg-white dark:bg-slate-900 border border-blue-200 dark:border-blue-800 rounded-xl shadow-2xl p-4 cursor-pointer transform transition-all duration-300 animate-slide-in hover:scale-[1.02]"
        >
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-blue-50 dark:bg-blue-950/50">
              {getCategoryMeta(latestToast).icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300">
                  {getCategoryMeta(latestToast).label}
                </span>
                <span className="text-[10px] text-gray-400">Just now</span>
              </div>
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100 mt-1 truncate">
                {latestToast.title}
              </p>
              <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 line-clamp-2">
                {latestToast.message}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* NOTIFICATION POPOVER PANEL */}
      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-40"
            onClick={() => setIsOpen(false)}
          />

          <div className="absolute right-0 top-full mt-2 w-96 max-w-[90vw] bg-white dark:bg-slate-900 rounded-xl shadow-2xl border border-gray-200 dark:border-slate-800 z-50 overflow-hidden flex flexDirection-column">
            {/* PANEL HEADER */}
            <div className="p-3.5 border-b border-gray-100 dark:border-slate-800 bg-gray-50/70 dark:bg-slate-800/50 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-sm text-gray-900 dark:text-white">
                  Notifications
                </h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 text-xs font-bold rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300">
                    {unreadCount} unread
                  </span>
                )}
              </div>

              <div className="flex items-center gap-1.5">
                <button
                  onClick={() => setSoundEnabled(!soundEnabled)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md transition-colors"
                  title={soundEnabled ? "Mute chimes" : "Enable chimes"}
                >
                  {soundEnabled ? (
                    <Volume2 className="w-4 h-4 text-blue-600" />
                  ) : (
                    <VolumeX className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                {unreadCount > 0 && (
                  <button
                    onClick={markAllAsRead}
                    className="flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 px-2 py-1 rounded-md hover:bg-blue-50 dark:hover:bg-blue-950/40 transition-colors"
                  >
                    <CheckCheck className="w-3.5 h-3.5" />
                    Mark read
                  </button>
                )}

                <button
                  onClick={() => setIsOpen(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded-md transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* CATEGORY FILTER TABS */}
            <div className="flex items-center gap-1 p-2 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto text-xs font-medium">
              {[
                { id: "all", label: "All" },
                { id: "assignments", label: "Leads" },
                { id: "followups", label: "Follow-ups" },
                { id: "payments", label: "Payments" },
                { id: "activities", label: "Activities" },
              ].map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-2.5 py-1 rounded-lg whitespace-nowrap transition-colors ${
                    activeTab === tab.id
                      ? "bg-blue-600 text-white font-semibold"
                      : "text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-slate-800"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* NOTIFICATION LIST */}
            <div className="max-h-80 overflow-y-auto divide-y divide-gray-100 dark:divide-slate-800">
              {loading ? (
                <div className="p-8 text-center text-xs text-gray-400">
                  Loading notifications...
                </div>
              ) : filteredNotifications.length === 0 ? (
                <div className="p-8 text-center">
                  <div className="w-10 h-10 mx-auto mb-2 rounded-full bg-gray-100 dark:bg-slate-800 flex items-center justify-center text-gray-400">
                    <Bell className="w-5 h-5" />
                  </div>
                  <p className="text-xs font-medium text-gray-500">
                    No notifications in this category
                  </p>
                </div>
              ) : (
                filteredNotifications.map((notif) => {
                  const meta = getCategoryMeta(notif);
                  const isUnread = !notif.read;

                  return (
                    <div
                      key={notif._id || notif.id}
                      onClick={() =>
                        markAsRead(
                          notif._id || notif.id,
                          notif.actionUrl || notif.action?.url
                        )
                      }
                      className={`p-3.5 transition-colors cursor-pointer group flex items-start gap-3 ${
                        isUnread
                          ? "bg-blue-50/50 dark:bg-blue-950/20 hover:bg-blue-50 dark:hover:bg-blue-950/40"
                          : "hover:bg-gray-50 dark:hover:bg-slate-800/50"
                      }`}
                    >
                      {/* Icon */}
                      <div className="p-2 rounded-lg bg-white dark:bg-slate-800 shadow-sm border border-gray-100 dark:border-slate-700 flex-shrink-0">
                        {meta.icon}
                      </div>

                      {/* Details */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span
                            className="text-[10px] font-semibold px-2 py-0.5 rounded-full"
                            style={{
                              backgroundColor: meta.badgeBg,
                              color: meta.badgeColor,
                            }}
                          >
                            {meta.label}
                          </span>
                          <span className="text-[10px] text-gray-400 flex-shrink-0">
                            {formatTime(notif.createdAt)}
                          </span>
                        </div>

                        <h4 className="text-xs font-semibold text-gray-900 dark:text-gray-100 mt-1 truncate">
                          {notif.title}
                        </h4>

                        <p className="text-xs text-gray-600 dark:text-gray-300 mt-0.5 leading-relaxed line-clamp-2">
                          {notif.message}
                        </p>

                        {(notif.actionUrl || notif.action?.url) && (
                          <div className="mt-2 flex items-center text-[11px] font-semibold text-blue-600 dark:text-blue-400 group-hover:underline">
                            View details <ExternalLink className="w-3 h-3 ml-1" />
                          </div>
                        )}
                      </div>

                      {/* Read indicator & Delete button */}
                      <div className="flex flex-col items-end gap-2 flex-shrink-0">
                        {isUnread && (
                          <span className="w-2 h-2 rounded-full bg-blue-600" />
                        )}
                        <button
                          onClick={(e) => deleteNotification(notif._id || notif.id, e)}
                          className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-rose-600 transition-opacity p-0.5"
                          title="Delete notification"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationBell;
